'use client';
import { ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import type { Locale } from '@/i18n/i18n';
import type { GalleryItems } from '@/lib/types';

type Props = {
  images?: GalleryItems | null;
  locale?: Locale;
  shouldShowInfo?: boolean;
  isRightSectionVisible: boolean;
  width?: number;
  hasImageFilter?: boolean;
  cssClass?: string;
};
export default function GalleryNew({
  images,
  locale = 'en-US',
  isRightSectionVisible,
  shouldShowInfo = true,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(
    () => new Set(),
  );
  const validImages = (images ?? []).filter(
    (image): image is NonNullable<GalleryItems[number]> & { url: string } =>
      Boolean(image?.url),
  );
  const selectedIndex = Math.min(
    activeIndex,
    Math.max(0, validImages.length - 1),
  );
  const active = validImages[selectedIndex];
  const greek = locale === 'el';
  if (!active?.url) return null;
  return (
    <section
      id={isRightSectionVisible ? 'destinations' : undefined}
      className="destinations editorial-section"
    >
      {isRightSectionVisible && (
        <div className="section-heading">
          <p className="eyebrow">
            {greek ? 'Ο ΤΟΠΟΣ ΜΑΣ' : 'OUR PART OF THE WORLD'}
          </p>
          <h2 className="section-title">
            {greek
              ? 'Κοντά σας.\nΌπου κι αν πάτε.'
              : 'A local perspective.\nA world to discover.'}
          </h2>
        </div>
      )}
      <div className="destination-layout">
        <div className="destination-photo">
          {validImages.map((image, index) => (
            <Image
              key={`${image.url}-${index}`}
              src={image.url}
              alt={
                displayedIndex === index
                  ? image.title ||
                    (greek
                      ? 'Προορισμός στην περιοχή μας'
                      : 'A local destination')
                  : ''
              }
              aria-hidden={displayedIndex !== index}
              className={`destination-image ${displayedIndex === index ? 'is-active' : ''}`}
              fill
              sizes="(max-width: 760px) 88vw, 45vw"
              onLoad={() => {
                setLoadedImages((loaded) => new Set(loaded).add(image.url));
                if (selectedIndex === index) setDisplayedIndex(index);
              }}
            />
          ))}
          <span className="photo-counter">
            {String(selectedIndex + 1).padStart(2, '0')} /{' '}
            {String(validImages.length).padStart(2, '0')}
          </span>
        </div>
        <div className="destination-details">
          <p className="destination-intro">
            {greek
              ? 'Από το κέντρο της Αλεξανδρούπολης μέχρι τον επόμενο προορισμό σας. Γνωρίζουμε τον δρόμο.'
              : 'From the heart of Alexandroupolis to your next escape. We know the way.'}
          </p>
          <div className="destination-list">
            {validImages.map((image, index) => (
              <button
                type="button"
                key={`${image.url}-${index}`}
                className={`destination-choice ${selectedIndex === index ? 'is-active' : ''}`}
                aria-pressed={selectedIndex === index}
                onClick={() => {
                  setActiveIndex(index);
                  if (loadedImages.has(image.url)) setDisplayedIndex(index);
                }}
              >
                <span className="destination-index">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>
                  {image.title ||
                    `${greek ? 'Προορισμός' : 'Destination'} ${index + 1}`}
                </span>
                <ArrowUpRight size={22} aria-hidden="true" />
              </button>
            ))}
          </div>
          {shouldShowInfo && active.description && (
            <p className="destination-caption" aria-live="polite">
              {active.description}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
